import { ClientProjectOverview } from '@models/client.model';
import { SubcontractorHealth } from '@models/subcontractor.model';
import { fillTemplate } from './clientProjectDisplay';

/**
 * Sprint 44, story-226. Formatting only: picks plural forms, rounds a percent, renders a
 * relative date. Nothing here counts or classifies tasks; every number comes from the server's
 * `ClientProjectOverview` row.
 */

export type ClientCounterKind =
  | 'overdue'
  | 'dueThisWeek'
  | 'awaitingApproval'
  | 'openIncidents';

/** Fixed display order of the counters on the card strip and the detail header. */
export const CLIENT_COUNTER_ORDER: ClientCounterKind[] = [
  'overdue',
  'dueThisWeek',
  'awaitingApproval',
  'openIncidents',
];

export const clientCounterValue = (
  overview: ClientProjectOverview,
  kind: ClientCounterKind,
): number => {
  switch (kind) {
    case 'overdue':
      return overview.overdueCount;
    case 'dueThisWeek':
      return overview.dueThisWeekCount;
    case 'awaitingApproval':
      return overview.awaitingClientApprovalCount;
    default:
      return overview.openIncidentsCount;
  }
};

/** Only the non-zero counters, in the fixed order. A zero is noise and never renders. */
export const nonZeroCounters = (
  overview: ClientProjectOverview,
): { kind: ClientCounterKind; count: number }[] =>
  CLIENT_COUNTER_ORDER.map((kind) => ({
    kind,
    count: clientCounterValue(overview, kind),
  })).filter((counter) => counter.count > 0);

/** `base` + `One`/`Other` catalog convention, `{n}` placeholder. */
export const pluralize = (
  catalog: Record<string, string>,
  base: string,
  n: number,
): string =>
  fillTemplate(catalog[`${base}${n === 1 ? 'One' : 'Other'}`] ?? '', { n });

export const healthBarVariant = (
  health: SubcontractorHealth,
): 'error' | 'success' | 'secondary' | 'primary' => {
  switch (health) {
    case SubcontractorHealth.BEHIND:
      return 'error';
    case SubcontractorHealth.ON_TRACK:
    case SubcontractorHealth.COMPLETE:
      return 'success';
    case SubcontractorHealth.NOT_STARTED:
      return 'secondary';
    default:
      return 'primary';
  }
};

/** 0..1 server completion to a whole percent for the bar. */
export const completionPercent = (completion: number): number =>
  Math.round(Math.min(Math.max(completion, 0), 1) * 100);

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * "today", "yesterday", "{n} days ago", then an absolute date after 30 days. `absolute` renders
 * the fallback date (locale formatting is the caller's).
 */
export const formatRelativeActivity = (
  iso: string,
  now: Date,
  labels: { today: string; yesterday: string; daysAgo: string },
  absolute: (date: Date) => string,
): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return labels.today;
  if (days === 1) return labels.yesterday;
  if (days <= 30) return fillTemplate(labels.daysAgo, { n: days });
  return absolute(date);
};
